import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { checkAnyUserType, checkUserType } from '@utils/Util';

import { toastError, toastSuccess } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import LoaderComponent from '@/components/LoaderComponent';

import VersionSidebar from './Components/VersionSidebar';
import Header from './Components/Header';
import CostingTableAndSummary from './Components/CostingTableAndSummary';
import {
	useGetCostingByProductIdQuery,
	useGetHistoryByProductIdQuery,
	useGetPriceRangeByProductIdQuery,
	useSubmitCostingMutation,
	useGetCostingByHistoryIdQuery,
	useLazyLikeUnlikeCostingQuery,
	useHandleRecostMutation,
	useGetProductInfoByProductIdQuery,
	useScenarioReCostMutation
} from './api/costingAgentApi';
import { validateRequiredFields as validateRequiredFieldsUtil } from './utils';
import MarketContextTarget from './Components/MarketContextTarget';
import CostingHeader from './Components/CostingHeader';
import OptimizeAndOffer from './Components/OptimizeAndOffer';

const CostingAgent = ({ setLoader = () => {}, showHeader = true }) => {
	const { id } = useParams();
	const [showVersionSidebar, setShowVersionSidebar] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [hasFileChanges, setHasFileChanges] = useState(false);
	const [formData, setFormData] = useState(null);
	const [selectedHistoryId, setSelectedHistoryId] = useState(null);
	const location = useLocation();
	const navigate = useNavigate();
	const hasProcessedInitialLoad = useRef(false);
	const [shouldFetch, setShouldFetch] = useState(false);
	const [isEmptyHistory, setIsEmptyHistory] = useState(false);
	const [changedSections, setChangedSections] = useState(new Set());
	const [changedFieldNames, setChangedFieldNames] = useState(new Set());
	const [hasSubmittedCosting, setHasSubmittedCosting] = useState(false);
	const [recostResponse, setRecostResponse] = useState(null);
	const [validationErrors, setValidationErrors] = useState({});
	const isProductDevelopment = checkUserType('PRODUCT_DEVELOPMENT');
	const [activeView, setActiveView] = useState(
		isProductDevelopment ? 'production-costing' : 'market-context'
	);
	const [isOcsOfferLoading, setIsOcsOfferLoading] = useState(false);
	const [isProductionCostingDisabled, setIsProductionCostingDisabled] = useState(false);
	const [orderQuantity, setOrderQuantity] = useState(null);
	const [isBaseScenarioSelected, setIsBaseScenarioSelected] = useState(false);
	const [costingBrandInfo, setCostingBrandInfo] = useState({
		brandId: null,
		brandName: null,
		singleQty: null,
		initialCostingId: null,
		setProductId: null,
		isBuyerRequest: true
	});
	const [selectedFileList, setSelectedFileList] = useState({});
	const dispatch = useDispatch();
	const editableRoles = () => {
		return checkAnyUserType(['ADMIN', 'PRODUCT_DEVELOPMENT', 'COSTING_MANAGER', 'ACCOUNT_MANAGER']);
	};
	const isEditable = editableRoles();
	const {
		data: costingData,
		// refetch: refetchAiCosting,
		isLoading,
		isFetching: isCostingFetching,
		error: costingError
	} = useGetCostingByProductIdQuery(
		{
			productId: id,
			brandName: costingBrandInfo?.brandName?.trim(),
			singleQty: costingBrandInfo?.singleQty || 1000,
			isBuyerRequest: costingBrandInfo?.isBuyerRequest || false,
			initialCostingId: costingBrandInfo?.initialCostingId || null
		},
		{ skip: !shouldFetch }
	);

	const {
		data: historyData,
		refetch: refetchHistory,
		isLoading: historyLoading
	} = useGetHistoryByProductIdQuery({
		productId: id,
		brandId:
			costingBrandInfo?.isBuyerRequest === true || costingBrandInfo?.isBuyerRequest === 'true'
				? new URLSearchParams(location.search).get('brandId')
				: '',
		initialCostingId: new URLSearchParams(location.search).get('initialCostingId')
	});
	const [submitCosting] = useSubmitCostingMutation();
	const [handleRecost] = useHandleRecostMutation();
	const [scenarioReCost] = useScenarioReCostMutation();
	const [likeUnlikeCosting, { isLoading: isLikeUnlikeLoading }] = useLazyLikeUnlikeCostingQuery();
	const {
		data: priceRangeData,
		isLoading: isPriceRangeLoading,
		error: priceRangeError
	} = useGetPriceRangeByProductIdQuery(id);
	const { data: productInfoData } = useGetProductInfoByProductIdQuery(id);

	const hasFileChange = () => {
		setHasChanges(true);
		setHasFileChanges(true);
	};

	useEffect(() => {
		const params = new URLSearchParams(location.search);
		const brandId = params.get('brandId') || '1';
		const brandName = params.get('brandName') || 'Zalando';
		const isBuyerRequest = params.get('isBuyerRequest') || false;
		// the URL value is a string, so 'false' has to be compared, not checked for truthiness
		const isNotBuyerRequest = String(isBuyerRequest) !== 'true';
		const singleQty = params.get('singleQty') || (isNotBuyerRequest && orderQuantity) || 1000;
		const initialCostingId = params.get('initialCostingId') || null;
		const setProductId = params.get('setProductId') || null;
		setCostingBrandInfo((prev) => ({
			...prev,
			brandId,
			brandName,
			singleQty,
			isBuyerRequest,
			initialCostingId,
			setProductId
		}));
	}, [location.search, orderQuantity]);

	useEffect(() => {}, [priceRangeData]);

	useEffect(() => {
		if (priceRangeError) {
			toastError('Error fetching price range data');
		}
	}, [priceRangeError]);

	// the history list comes newest first — a reload opens the latest (re-costed) version, not an older approved one
	const getHistoryId = () => {
		if (!historyData?.payload || historyData.payload.length === 0) {
			return null;
		}
		// const approvedItem = historyData.payload.find((item) => item.approved === true);
		// return approvedItem ? approvedItem.id : historyData.payload[0]?.id;
		return historyData.payload[0]?.id;
	};
	const historyId = selectedHistoryId || getHistoryId();
	const {
		data: historyCostingData,
		refetch: refetchHistoryCosting,
		isLoading: isHistoryCostingLoading
	} = useGetCostingByHistoryIdQuery({ productId: id, historyId }, { skip: !historyId });

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length === 0) {
			setIsEmptyHistory(true);
		} else {
			setIsEmptyHistory(false);
		}
	}, [historyData]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setHasSubmittedCosting(historyCostingData?.payload?.isSubmitted || false);
		}
	}, [historyCostingData, selectedHistoryId]);

	useEffect(() => {
		if (historyData?.payload && historyData.payload.length > 0 && !selectedHistoryId) {
			setSelectedHistoryId(getHistoryId());
		}
	}, [historyData, selectedHistoryId]);

	useEffect(() => {
		setLoader(false);
	}, [id, setLoader]);

	useEffect(() => {
		if (costingError) {
			toastError(costingError?.data?.message || 'Error fetching costing data');
		}
	}, [costingError]);

	//after fetching costing data, set shouldFetch to false
	useEffect(() => {
		if (shouldFetch && !isCostingFetching && (costingData || costingError)) {
			setShouldFetch(false);
		}
	}, [shouldFetch, isCostingFetching, costingData, costingError]);

	useEffect(() => {
		if (!hasProcessedInitialLoad.current && isEmptyHistory) {
			const params = new URLSearchParams(location.search);
			setShouldFetch(true);
			hasProcessedInitialLoad.current = true;
			if (params.has('isAiCosting')) {
				params.delete('isAiCosting');
				navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
			}
		}
	}, [location.search, location.pathname, navigate, isEmptyHistory]);

	useEffect(() => {
		if (costingData?.payload) {
			const payload = costingData?.payload;
			setFormData({ ...payload.costingDetails });
			setSelectedHistoryId(payload?.id);
			setHasChanges(false);
			setChangedSections(new Set());
			setChangedFieldNames(new Set());
			setRecostResponse(null);
			refetchHistory();
		}
	}, [costingData, refetchHistory]);

	useEffect(() => {
		if (historyCostingData?.payload) {
			setFormData({ ...historyCostingData.payload.costingDetails });
			setHasChanges(false);
			setChangedSections(new Set());
		}
	}, [historyCostingData]);

	const sectionToChangeType = {
		supplierChange: 'SUPPLIER_CHANGE',
		fabricWisePriceBreakdownList: 'FABRIC_CHANGE',
		fiberRequirementList: 'SUSTAINABILITY_CHANGE',
		printPriceList: 'PRINT_CHANGE',
		embroideryPriceList: 'EMBROIDERY_CHANGE',
		washPriceBreakdown: 'WASH_CHANGE',
		trimsDetailsList: 'TRIMS_CHANGE',
		trimsAdditionalCharge: 'TRIMS_CHANGE',
		packagingPriceCostBreakdown: 'PACKAGING_CHANGE',
		cmCost: 'CM_CHANGE',
		commercialCostPercentage: 'COMMERCIAL_CHANGE',
		perStyleTestingCost: 'TESTING_COST_CHANGE'
	};

	const handleDataChange = useCallback((section) => {
		setHasChanges(true);
		if (section && sectionToChangeType[section]) {
			setChangedSections((prev) => new Set([...prev, sectionToChangeType[section]]));
		}
	}, []);

	// Store the name of any input field the user newly typed into
	const trackChangedField = useCallback((fieldName) => {
		if (!fieldName) return;
		setChangedFieldNames((prev) => new Set([...prev, fieldName]));
	}, []);

	const validateRequiredFields = () => {
		const errors = validateRequiredFieldsUtil(formData);
		setValidationErrors(errors);
		return Object.keys(errors).length === 0;
	};

	const processSelectedFiles = (files) => {
		return (
			files?.map((item) => ({
				docId: item.id,
				docName: item.label,
				docUrl: item.clickAbleLink,
				sheetName: item?.sheetName || null
			})) || []
		);
	};

	const reCostIndividually = async (changeType, data) => {
		dispatch(openLoader());
		try {
			let requestBody = {
				techpackCostingDataDTO: { ...formData },
				changeTypeList: [changeType]
			};
			requestBody.changedFieldList = Array.from(changedFieldNames);
			if (changeType === 'CAD_WASTAGE_PERCENTAGE_CHANGE') {
				requestBody.techpackCostingDataDTO.cadWiseConsumptionList = data;
			} else if (changeType === 'MEASUREMENT_CHANGE') {
				requestBody.techpackCostingDataDTO.fabricWiseMeasurements = data;
			} else if (changeType === 'WASH_CHANGE') {
				requestBody.techpackCostingDataDTO.washPriceBreakdown = data;
			} else if (changeType === 'EMBROIDERY_CHANGE') {
				requestBody.techpackCostingDataDTO.embroideryPriceList = data;
			} else if (changeType === 'PRINT_CHANGE') {
				requestBody.techpackCostingDataDTO.printPriceList = data;
			} else if (changeType === 'FABRIC_CHANGE') {
				requestBody.techpackCostingDataDTO.fabricWisePriceBreakdownList = data;
			} else if (changeType === 'FILE_CHANGE') {
				let patternIds = selectedFileList?.['Pattern']
					? Array.from(selectedFileList?.['Pattern'])
					: [];
				let techpackIds = selectedFileList?.['Techpack']
					? Array.from(selectedFileList?.['Techpack'])
					: [];
				let otherIds = selectedFileList?.['Others'] ? Array.from(selectedFileList?.['Others']) : [];
				let measurements = selectedFileList?.['Measurement']
					? Array.from(selectedFileList?.['Measurement'])
					: [];
				if (
					techpackIds?.length === 0 &&
					costingBrandInfo?.isBuyerRequest &&
					!isShowNitexTechpackLabel
				) {
					return toastError('Please select at least one techpack file to recost');
				}

				if (
					(!historyId || !formData || changedSections.size === 0) &&
					patternIds.length === 0 &&
					techpackIds.length === 0 &&
					measurements.length === 0 &&
					otherIds.length === 0
				)
					return;

				if (patternIds.length > 0 || techpackIds.length > 0 || measurements.length > 0) {
					requestBody = {
						...requestBody,
						patternFiles: processSelectedFiles(patternIds),
						techpackFiles: processSelectedFiles(techpackIds),
						measurementFiles: processSelectedFiles(measurements),
						otherFiles: processSelectedFiles(otherIds)
					};
				}
			}

			const response = await handleRecost({
				productId: id,
				historyId,
				brandName: costingBrandInfo?.brandName?.trim(),
				singleQty: costingBrandInfo?.singleQty || 1000,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				requestBody: requestBody,
				initialCostingId: costingBrandInfo?.initialCostingId
			}).unwrap();

			if (response?.payload) {
				setFormData({ ...response.payload.costingDetails });
				setRecostResponse(response.payload);
				setSelectedHistoryId(response.payload.id);
				setValidationErrors({});
				refetchHistory();
				setChangedFieldNames(new Set());
				toastSuccess('Re-costed Successful');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating Measurement');
		} finally {
			setChangedFieldNames(new Set());
			dispatch(closeLoader());
		}
	};

	const onReCost = async () => {
		dispatch(openLoader());
		const changedList = Array.from(changedSections);
		if (changedList.length === 0 && !hasFileChanges) {
			dispatch(closeLoader());
			return;
		}
		if (hasFileChanges) {
			changedList.push('FILE_CHANGE');
		}
		try {
			let requestBody = {
				techpackCostingDataDTO: { ...formData },
				changeTypeList: changedList
			};
			requestBody.changedFieldList = Array.from(changedFieldNames);

			if (hasFileChanges) {
				let patternIds = selectedFileList?.['Pattern']
					? Array.from(selectedFileList?.['Pattern'])
					: [];
				let techpackIds = selectedFileList?.['Techpack']
					? Array.from(selectedFileList?.['Techpack'])
					: [];
				let otherIds = selectedFileList?.['Others'] ? Array.from(selectedFileList?.['Others']) : [];
				let measurements = selectedFileList?.['Measurement']
					? Array.from(selectedFileList?.['Measurement'])
					: [];
				if (
					techpackIds?.length === 0 &&
					costingBrandInfo?.isBuyerRequest &&
					!isShowNitexTechpackLabel
				) {
					return toastError('Please select at least one techpack file to recost');
				}

				if (
					(!historyId || !formData || changedSections.size === 0) &&
					patternIds.length === 0 &&
					techpackIds.length === 0 &&
					measurements.length === 0 &&
					otherIds.length === 0
				)
					return;

				if (patternIds.length > 0 || techpackIds.length > 0 || measurements.length > 0) {
					requestBody = {
						...requestBody,
						patternFiles: processSelectedFiles(patternIds),
						techpackFiles: processSelectedFiles(techpackIds),
						measurementFiles: processSelectedFiles(measurements),
						otherFiles: processSelectedFiles(otherIds)
					};
				}
			}

			const response = await handleRecost({
				productId: id,
				historyId,
				brandName: costingBrandInfo?.brandName?.trim(),
				singleQty: costingBrandInfo?.singleQty || 1000,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				requestBody: requestBody,
				initialCostingId: costingBrandInfo?.initialCostingId
			}).unwrap();

			if (response?.payload) {
				setFormData({ ...response.payload.costingDetails });
				setRecostResponse(response.payload);
				setSelectedHistoryId(response.payload.id);
				setValidationErrors({});
				refetchHistory();
				setHasFileChanges(false);
				setChangedFieldNames(new Set());
				toastSuccess('Re-costed Successful');
			}
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating Measurement');
		} finally {
			setChangedFieldNames(new Set());
			dispatch(closeLoader());
		}
	};

	///scenario wise recost
	// costingId is the selected scenario's own costing id — scenarioCostingDetails can fall back to the base payload
	const onScenarioReCost = async (
		scenarioCostingDetails,
		changeTypeList = [],
		changedFieldList = [],
		costingId
	) => {
		if (changeTypeList.length === 0) return null;

		dispatch(openLoader());
		try {
			const response = await scenarioReCost({
				productId: id,
				initialCostingId: costingBrandInfo?.initialCostingId,
				singleQty: costingBrandInfo?.singleQty,
				isBuyerRequest: costingBrandInfo?.isBuyerRequest,
				brandName: costingBrandInfo?.brandName,
				requestBody: {
					costingId: costingId,
					techpackCostingDataDTO: { ...scenarioCostingDetails },
					changeTypeList,
					changedFieldList
				}
			}).unwrap();

			toastSuccess('Re-costed Successful');
			return response?.payload ?? response;
		} catch (error) {
			toastError(error?.data?.message || 'Error recalculating Scenario');
			return null;
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleSubmit = async () => {
		if (!historyId) return;

		if (!validateRequiredFields()) {
			toastError('Please fill in all required fields');
			return;
		}

		dispatch(openLoader());
		try {
			await submitCosting({
				productId: id,
				historyId,
				requestBody: formData,
				initialCostingId: costingBrandInfo?.initialCostingId,
				setProductId: costingBrandInfo?.setProductId
			}).unwrap();
			setHasSubmittedCosting(true);
			setValidationErrors({});
			refetchHistoryCosting();
			toastSuccess('Costing submitted successfully');
		} catch (error) {
			toastError(error.data.message);
		} finally {
			dispatch(closeLoader());
		}
	};

	const handleLikeUnlike = async (isLiked) => {
		if (!historyId) return;
		try {
			await likeUnlikeCosting({ productId: id, historyId, isLiked });
			refetchHistory();
		} catch (error) {
			toastError(error?.data?.message || 'Failed to update like status');
		}
	};

	const getLikeStatus = () => {
		if (!historyData?.payload || !historyId) return null;
		const selectedItem = historyData.payload.find((item) => item.id === historyId);
		return selectedItem?.isLiked ?? null;
	};

	const isLiked = getLikeStatus();

	const updateFormData = useCallback(
		(section, data) => {
			setFormData((prev) => {
				if (!prev) return prev;
				return {
					...prev,
					[section]: data
				};
			});
			handleDataChange(section);
		},
		[handleDataChange]
	);

	const isShowNitexTechpackLabel =
		recostResponse?.payload?.isNitexTechpack === true ||
		costingData?.payload?.isNitexTechpack === true ||
		historyCostingData?.payload?.isNitexTechpack === true;

	const usedDocuments =
		recostResponse?.payload?.usedDocumentIds ||
		costingData?.payload?.usedDocumentIds ||
		historyCostingData?.payload?.usedDocumentIds;

	const responsePayload =
		recostResponse?.costingDetails ||
		costingData?.payload.costingDetails ||
		historyCostingData?.payload.costingDetails;
	const payload = formData || responsePayload?.costingDetails || {};

	const openTechpack = () => {
		const url = window.location.href;
		const separator = url.includes('?') ? '&' : '?';
		window.open(`${url}${separator}isOpenTechpackViewModal=true`, '_blank');
	};

	return (
		<LoaderComponent loading={isHistoryCostingLoading || historyLoading || isOcsOfferLoading}>
			<div className="costing-agent-container bg-white">
				{showHeader && (
					<Header
						onHistoryClick={() => setShowVersionSidebar(true)}
						costingBrandInfo={costingBrandInfo}
						baseQty={payload?.quantity}
						selectedFileList={selectedFileList}
						setSelectedFileList={setSelectedFileList}
						usedDocumentIds={usedDocuments}
						isShowNitexTechpackLabel={isShowNitexTechpackLabel}
						isFileChanged={hasFileChanges}
						onEyeClick={openTechpack}
						reCostChange={reCostIndividually}
						hasFileChange={hasFileChange}
						isShowDownload={hasSubmittedCosting}
						versionId={selectedHistoryId}
						onViewChange={setActiveView}
						activeView={activeView}
						productionIsloading={Boolean(isLoading || isCostingFetching)}
						isProductionCostingDisabled={isProductionCostingDisabled}
						isProductDevelopment={isProductDevelopment}
						isBaseScenarioSelected={isBaseScenarioSelected}
					/>
				)}

				{activeView === 'market-context' ? (
					<MarketContextTarget
						costingBrandInfo={costingBrandInfo}
						productId={id}
						onViewChange={setActiveView}
						productionIsloading={Boolean(isLoading || isCostingFetching)}
						onProductionCostingDisabledChange={setIsProductionCostingDisabled}
					/>
				) : activeView === 'optimize-offer' ? (
					<OptimizeAndOffer
						costingBrandInfo={costingBrandInfo}
						productId={id}
						historyDetailId={payload?.id}
						historyId={historyId}
						payload={payload}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onScenarioReCost={onScenarioReCost}
						onHistoryRefetch={refetchHistory}
						onCostingRefetch={refetchHistoryCosting}
						onOrderQuantityChange={setOrderQuantity}
						onBaseScenarioSelectedChange={setIsBaseScenarioSelected}
					/>
				) : (
					<div className="costing-table-container relative">
						{activeView !== 'optimize-offer' && !isProductDevelopment && (
							<CostingHeader
								productId={id}
								initialCostingId={costingBrandInfo?.initialCostingId}
								historyId={historyId}
								totalFobPerUnit={payload?.predictedCost || 0}
								baseQty={payload?.quantity}
								quantityWisePrices={payload?.qtyWisePriceList || []}
								onHistoryRefetch={refetchHistory}
								onCostingRefetch={refetchHistoryCosting}
								historyDetailId={payload?.id}
								onOcsOfferLoadingChange={setIsOcsOfferLoading}
								onOptimizeOffer={() => setActiveView('optimize-offer')}
								costingBrandInfo={costingBrandInfo}
								onOrderQuantityChange={setOrderQuantity}
								costingId={payload?.id}
							/>
						)}
						<CostingTableAndSummary
							payload={payload}
							responsePayload={responsePayload}
							updateFormData={updateFormData}
							isEditable={isEditable}
							validationErrors={validationErrors}
							reCostIndividually={reCostIndividually}
							trackChangedField={trackChangedField}
							hasChanges={hasChanges}
							handleSubmit={handleSubmit}
							hasSubmittedCosting={hasSubmittedCosting}
							onReCost={onReCost}
							historyId={historyId}
							isLiked={isLiked}
							handleLikeUnlike={handleLikeUnlike}
							isLikeUnlikeLoading={isLikeUnlikeLoading}
							costingBrandInfo={costingBrandInfo}
							priceRangeData={priceRangeData}
							isPriceRangeLoading={isPriceRangeLoading}
							productInfoData={productInfoData}
							isProductDevelopment={isProductDevelopment}
							productionIsLoading={Boolean(isLoading || isCostingFetching)}
							headerOrderQuantity={orderQuantity}
						/>
					</div>
				)}
				{showVersionSidebar && (
					<VersionSidebar
						productId={id}
						selectedHistoryId={selectedHistoryId}
						onHistorySelect={setSelectedHistoryId}
						onClose={() => setShowVersionSidebar(false)}
						brandId={costingBrandInfo?.isBuyerRequest ? costingBrandInfo?.brandId : null}
						initialCostingId={
							costingBrandInfo?.isBuyerRequest ? costingBrandInfo?.initialCostingId : null
						}
					/>
				)}
			</div>
		</LoaderComponent>
	);
};

export default CostingAgent;
